import { useEffect, useRef } from "react";
import { LocationState } from "../types";

/**
 * Custom hook for managing scroll behavior based on route changes.
 * Scrolls to hash anchors with smooth behavior support and scroll to top on route change.
 */
export const useScrollBehavior = (location: LocationState, shouldSkipScroll = false) => {
  const isFirstRenderRef = useRef(true);

  // Scroll to hash anchor when hash changes (unless scrolling should be skipped)
  useEffect(() => {
    if (shouldSkipScroll) return;

    const hash = location.hash;
    if (!hash) return;

    // Delay to allow lazy-mounted content to render first
    const timer = setTimeout(() => {
      const target = document.querySelector(hash);
      if (!target) return;

      // Respect user's motion preferences
      const prefersReduced =
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;

      target.scrollIntoView({
        behavior: prefersReduced ? "auto" : "smooth",
        block: "start",
      });
    }, 0);

    return () => clearTimeout(timer);
  }, [location.hash, shouldSkipScroll]);

  // Scroll to top on pathname change (unless hash is present or first render)
  useEffect(() => {
    if (location.hash) return;

    // Skip scroll-to-top on initial mount
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    // Add delay to ensure LazyMount content has settled
    const timer = setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    }, 50);

    return () => clearTimeout(timer);
  }, [location.pathname, location.hash]);
};
